import type { LocaleContent, SharedSiteConfig } from "@/types/site";

export interface BreadcrumbItem {
  label: string;
  url: string;
}

export function organizationJsonLd(
  shared: SharedSiteConfig,
  content: LocaleContent,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    description: content.site.description,
    legalName: shared.organization?.legalName ?? content.site.name,
    logo: shared.organization?.logo,
    name: content.site.name,
    url: shared.organization?.url,
  };
}

export function personJsonLd(
  shared: SharedSiteConfig,
  content: LocaleContent,
): Record<string, unknown> {
  const knowsAbout = Array.from(
    new Set([
      ...content.experience.items.flatMap((item) => item.technologies),
      ...content.projects.items.flatMap((item) => item.technologies),
      ...content.skillsPage.groups.flatMap((group) => group.items),
    ]),
  );

  return {
    "@context": "https://schema.org",
    "@type": "Person",
    alumniOf: "University of Puerto Rico at Mayagüez",
    description: content.site.description,
    email: content.contactDetails?.email,
    knowsAbout,
    name: shared.organization?.legalName ?? content.site.name,
    telephone: content.contact.methods.find((method) =>
      method.label.match(/phone|teléfono/i),
    )?.value,
    url: shared.organization?.url,
  };
}

export function websiteJsonLd(
  shared: SharedSiteConfig,
  content: LocaleContent,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    description: content.site.description,
    inLanguage: shared.locales,
    name: content.site.name,
    url: shared.organization?.url,
  };
}

export function breadcrumbJsonLd(
  items: BreadcrumbItem[],
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      item: item.url,
      name: item.label,
      position: index + 1,
    })),
  };
}

export function contactPageJsonLd(
  content: LocaleContent,
  url: string,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    description: content.pages.contact.description,
    name: content.pages.contact.title,
    url,
  };
}
